import { Response } from 'miragejs';

import { SEARCH_PARAM } from '@api/constants';

const isLanguageAvailable = ({ model, language }) =>
  Object.keys(model.attrs[language]?.draftContent || {}).length !== 0;

export const getPaginatedEntityList = ({
  resource,
  paginatedSerializer = 'paginated-resource',
  searchInKeys = ['title'],
}) =>
  function paginate(schema, request) {
    const { [SEARCH_PARAM.QUERY]: query } = request.queryParams;
    let items = schema[resource].all();

    if (query) {
      items = items.filter((item) =>
        Object.entries(item.attrs).some(
          ([key, value]) =>
            searchInKeys.includes(key) &&
            typeof value === 'string' &&
            // TODO: missing searching in versioned entities
            value.toLowerCase().indexOf(query.toLowerCase()) !== -1,
        ),
      );
    }
    const json = this.serialize(items, paginatedSerializer);

    return new Response(200, { 'X-Total-Count': json.total }, json.data);
  };

export const getVersionedEntity = ({ modelName, serializer = 'versioned-resource' }) =>
  function get(schema, request) {
    const { id, language = 'en' } = request.params;

    const model = schema[modelName].find(id);

    if (!model || !isLanguageAvailable({ model, language })) {
      return new Response(404);
    }

    return this.serialize(model, serializer);
  };

export const updateVersionedEntity = ({ modelName, serializer = 'versioned-resource' }) =>
  function update(schema, request) {
    const { id, language = 'en' } = request.params;
    const attrs = JSON.parse(request.requestBody);

    const model = schema[modelName].find(id);

    if (!model) {
      return new Response(404);
    }

    const updatedModel = model.update({
      modified: new Date().toISOString(),
      modifiedBy: schema.users.first(),
      [language]: {
        ...model.attrs[language],
        draftContent: attrs,
      },
    });

    return this.serialize(updatedModel, serializer);
  };

export const publishVersionedEntity = ({ modelName, serializer = 'versioned-resource' }) =>
  function publish(schema, request) {
    const { id, language = 'en' } = request.params;

    const model = schema[modelName].find(id);

    if (!model || !isLanguageAvailable({ model, language })) {
      return new Response(404);
    }

    const updatedModel = model.update({
      published: new Date().toISOString(),
      publishedBy: schema.users.first(),
      [language]: {
        ...model.attrs[language],
        publishedContent: model.attrs[language].draftContent,
      },
    });

    return this.serialize(updatedModel, serializer);
  };

export const resourceWithPagination = (context, { except = [], resource, ...options }) => {
  context.resource(resource, { except: ['index', ...except] });
  context.get(`/${resource}`, getPaginatedEntityList({ resource, ...options }));
};

export const versionedResourceWithPagination = (
  context,
  {
    resource,
    modelName = resource,
    serializer = resource.slice(0, -1),
    paginatedSerializer = `paginated-${resource}`,
    searchInKeys,
  },
) => {
  context.get(`/${resource}`, getPaginatedEntityList({ resource, paginatedSerializer, searchInKeys }));

  const commonOptions = { modelName, serializer };
  context.get(`${resource}/:id`, getVersionedEntity(commonOptions));
  context.get(`${resource}/:id/:language`, getVersionedEntity(commonOptions));
  context.put(`${resource}/:id`, updateVersionedEntity(commonOptions));
  context.put(`${resource}/:id/:language`, updateVersionedEntity(commonOptions));
  context.put(`${resource}/:id/:language/publish`, publishVersionedEntity(commonOptions));
};
